<script>
const NODE_TAGS = {
  paragraph: 'p',
  blockquote: 'blockquote',
  ordered_list: 'ol',
  bullet_list: 'ul',
  list_item: 'li',
  horizontal_rule: 'hr',
  hard_break: 'br',
  image: 'img'
}

const MARK_TAGS = {
  bold: 'strong',
  strong: 'strong',
  italic: 'i',
  strike: 's',
  underline: 'u',
  code: 'code',
  link: 'a',
  styled: 'span'
}

const CUSTOM_COMPONENTS = {}

const getBlokComponent = (blokItem) => {
  const componentName = blokItem?.component
  if (!componentName) {
    return 'div'
  }
  return CUSTOM_COMPONENTS[componentName] || 'div'
}

const renderTextWithMarks = (h, node, key) => {
  const text = node.text || ''
  const marks = Array.isArray(node.marks) ? node.marks : []

  if (!marks.length) {
    return text
  }

  const sortedMarks = [...marks].sort((a, b) => {
    if (a.type === 'link' && b.type !== 'link') {
      return 1
    }
    if (b.type === 'link' && a.type !== 'link') {
      return -1
    }
    return 0
  })

  let vnode = text

  for (const mark of sortedMarks) {
    const tag = MARK_TAGS[mark.type] || 'span'
    const data = { key }

    if (mark.type === 'link') {
      const href = mark.attrs?.href || ''
      const target = mark.attrs?.target

      data.attrs = { href }
      if (target) {
        data.attrs.target = target
      }
      if (target === '_blank') {
        data.attrs.rel = 'noopener noreferrer'
      }
    }

    if (mark.type === 'styled') {
      const klass = mark.attrs?.class
      if (klass) {
        data.class = klass
      }
    }

    vnode = h(tag, data, [vnode])
  }

  return vnode
}

const renderNodes = (h, nodes, keyPrefix) => {
  const out = []

  for (const [index, node] of nodes.entries()) {
    const key = `${keyPrefix}-${index}`
    const vnode = renderNode(h, node, key)

    if (Array.isArray(vnode)) {
      for (const child of vnode) {
        out.push(child)
      }
      continue
    }

    out.push(vnode)
  }

  return out
}

const renderNode = (h, node, key) => {
  if (node.type === 'blok' && Array.isArray(node.attrs?.body)) {
    return node.attrs?.body.map((blokItem, blokIndex) => {
      const Comp = getBlokComponent(blokItem)
      return h(Comp, { key: `${key}-blok-${blokIndex}`, props: { body: blokItem } })
    })
  }

  if (node.type === 'text') {
    return renderTextWithMarks(h, node, key)
  }

  if (node.type === 'heading') {
    const level = node.attrs?.level
    const tag = level ? `h${level}` : 'h2'
    return h(tag, { key }, renderNodes(h, node.content || [], key))
  }

  if (node.type === 'code_block') {
    return h('pre', { key }, [
      h('code', {}, renderNodes(h, node.content || [], `${key}-code`))
    ])
  }

  if (node.type === 'image') {
    const src = node.attrs?.src || ''
    const alt = node.attrs?.alt || ''
    const title = node.attrs?.title
    const attrs = { src, alt }
    if (title) {
      attrs.title = title
    }
    return h('img', { key, attrs })
  }

  if (node.type === 'horizontal_rule') {
    return h('hr', { key })
  }

  if (node.type === 'hard_break') {
    return h('br', { key })
  }

  const tag = NODE_TAGS[node.type] || 'div'
  const children = renderNodes(h, node.content || [], key)
  return h(tag, { key }, children)
}

export default {
  name: 'RichTextRenderer',
  functional: true,
  props: {
    document: {
      type: Object,
      required: true
    }
  },
  render (h, ctx) {
    const data = { ...ctx.data }
    delete data.props

    const nodes = ctx.props?.document?.content || []
    return h('div', data, renderNodes(h, nodes, 'rt'))
  }
}
</script>
